import { formatDate } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl, ValidationErrors, FormBuilder } from '@angular/forms';

import Swal from 'sweetalert2';
import { Subscription } from 'rxjs';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { Companie } from 'src/app/core/models/companie.model';
import { InfoMotel } from 'src/app/core/models/infoMotel.model';
import { SwalService } from 'src/app/shared/services/swal.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { CompanieService } from 'src/app/core/services/companie.service';

@Component({
  selector: 'app-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.css']
})
export class InfoComponent implements OnInit, OnDestroy {
  actualId: string;
  infoForm: FormGroup;
  infoMotel: InfoMotel;
  submitMode = false;
  loaderConfig: any;
  actualCompany: Companie;
  routeSubscription: Subscription;
  lastCheckFormatted: string;
  dataLimiteFormatted: string;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private swalService: SwalService,
    private utilsService: UtilsService,
    private loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
    private companieService: CompanieService,
    private ngxUiLoaderService: NgxUiLoaderService,
  ) { }

  ngOnInit() {
    this.setCompanie();
    this.loaderConfig = this.loaderService.getDefaultLoaderConfig();
  }

  /**
   * Pega o id da URL e armazena no `this.actualId`
   */
  getActualId() {
    this.routeSubscription = this.activatedRoute.params.subscribe((params: Params) => {
      this.actualId = params.id;
    });
  }

  /**
   * Seta `this.actualCompany` com a Empresa armazenada no `history.state`.
   * Caso não existe essa informação ela busca da API
   * Depois traz as informações de configuração do InfoMotel atrelados com a
   * Empresa, constrói o formulário e seta os dados das tabelas
   */
  async setCompanie() {
    this.ngxUiLoaderService.start();

    this.getActualId();

    this.actualCompany = await this.utilsService.setCompanie(this.actualId);

    await this.getInfoMotel();
    
    this.buidInfoForm();

    this.ngxUiLoaderService.stop();
  }

  /**
   * Armazena as informações de configuração do
   * InfoMotel da Empresa no `this.infoMotel`
   */
  async getInfoMotel() {
    this.getActualId();

    const infoMotelResponse = await this.companieService.getInfoMotelByCompany(this.actualId);
    
    this.infoMotel = infoMotelResponse;
  }

  /**
   * Contrói o Formulário do InfoMotel
   */
  buidInfoForm() {
    let objLicenceJson = null; 
    let objConfigJson = null; 
    
    try { 
      const tempLicenceJson = JSON.parse(this.infoMotel.iMotelLicenceJson);

      objLicenceJson = tempLicenceJson;
    } catch {
      objLicenceJson = null;
    }

    try { 
      const tempConfigJson = JSON.parse(this.infoMotel.iMotelConfigJson);

      objConfigJson = tempConfigJson;
    } catch {
      objConfigJson = null;
    }
    this.infoForm = this.formBuilder.group({
      id: new FormControl(this.infoMotel.id),
      iMotelLicenceExpirationDate: new FormControl(
        formatDate((this.infoMotel.iMotelLicenceExpirationDate ?? this.actualCompany.createdAt), 'yyyy-MM-dd', 'en', '+0000'), 
        Validators.required
      ),
      iMotelLicenceJson: new FormControl(
        JSON.stringify(objLicenceJson, null, 4), 
        Validators.compose([
          Validators.required, 
          this.jsonValidator()
        ])
      ),
      iMotelConfigJson: new FormControl(
        JSON.stringify(objConfigJson, null, 4), 
        Validators.compose([
          Validators.required, 
          this.jsonValidator()
        ])
      ),
      iMotelVersion: new FormControl(this.infoMotel.iMotelVersion),
    });

    this.lastCheckFormatted = this.dateFormat(this.infoMotel.iMotelLastCheck, false);
  }

    /**
   * Traz uma string formatada da data sendo usada
   *
   * @param {string} value
   * @param {boolean=} justDate
   */
     dateFormat(value: string, justDate?: boolean): string {
      if (!value) return null;
  
      let string = value.replace(/-/g, '/').replace(/T/g, ' ').substr(0, 19);
  
      if (justDate) {
        string = value.replace(/-/g, '/').replace(/T/g, ' ').substr(0, 10);
  
        return formatDate(string, 'dd/MM/yyyy', 'pt');
      }
  
      return formatDate(string, 'dd/MM/yyyy HH:mm:ss', 'pt', '-0600');
    }

  /**
   * Valida se informação passada tem a
   * possibilidade de ser transformada para JSON
   */
  jsonValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const error: ValidationErrors = { jsonInvalid: true };

      try {
        JSON.parse(control.value);
      } catch {
        return error;
      }

      return null;
    };
  }

  async removeAllData() {
    const res = await Swal.fire({
      type: 'warning',
      text: "Esta operação exclui permanentemente todos os dados desta empresa. Tem certeza que deseja continuar?",
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar'
    });
    
    if (res.value) {
      const result = await this.companieService.removeAllData(this.actualId);
      this.swalService.fireSuccessSwal(result.message);
    }
  }

  async onSubmit() {
    this.submitMode = true;

    if (this.infoForm.invalid) {
      return this.swalService.fireErrorSwal('É necessário preencher todos campos!');
    }

    this.getActualId();

    const objectInfoMotel = this.infoForm.value;

    const expirationDate = new Date(objectInfoMotel.iMotelLicenceExpirationDate);

    objectInfoMotel.iMotelLicenceExpirationDate = expirationDate.toISOString();

    const confirmModal = await this.swalService.fireConfirmSwal();

    if (confirmModal.value) {
      const responseInfoMotel = await this.companieService.updateInfoMotel(this.actualId, objectInfoMotel);

      if (responseInfoMotel) {
        this.swalService.fireSuccessSwal(responseInfoMotel.message);
      }

    }

    this.router.navigate(['/']);
    
    this.submitMode = false;
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

}
